import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ConfirmationModal from '../../../components/task/ConfirmationModal'
import { useAlert } from 'react-alert'
import { useHistory, useParams } from 'react-router-dom'
import { setPath, setUrlId } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import { destroyTaskProjectTemplateSetting, fetchTaskProjectTemplateSetting } from '../../../../states/containers/task/taskSlice'
import { RequestGetTaskProjectTemplateSetting } from '../../../../states/types/task'

interface ConfirmationModalState {
  display: boolean
  type: string
  name: string
  callback: VoidFunction
}

interface TemplateDetail {
  id?: number
  name: string
  code: string | null
  color: string
  description: string | null
  permission: {
    viewable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
    usable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
  }
  labels: {
    name: string
  }[]
}

const Index: FC = () => {
  const {
    task,
  } = useSelector((state: RootState) => state)

  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()
  const { language } = useParams<{ language: LangValue }>()

  const [data, setData] = useState<TemplateDetail[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  // confirmation modal
  const openConfirmationModal = (type: string, id: number, name: string) => {
    let callback = () => {
      // none
    }
    switch (type) {
      case "delete":
        callback = () => {
          dispatch(
            destroyTaskProjectTemplateSetting(
              { id: id.toString() },
              () => {
                alert.success("ユーザーテンプレートを削除しました！")
              },
            ),
          )
        }
        break
      default:
        break
    }
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: name || "",
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const createTemplate = () => {
    history.push({
      pathname: `${setPath(URL.TASK_PROJECT_TEMPLATE_SETTING_CREATE.path, language)}`,
    })
  }

  const updateTemplate = (id: number) => {
    history.push({
      pathname: `${setPath(setUrlId(URL.TASK_PROJECT_TEMPLATE_SETTING_DETAIL.path, 'id', id), "ja")}`,
      state: { keep: true },
    })
  }

  useEffect(() => {
    const params: RequestGetTaskProjectTemplateSetting = {
      limit: 1000,
    }
    dispatch(
      fetchTaskProjectTemplateSetting(
        params,
        () => {
          setIsLoading(false)
        },
      ),
    )
  }, [])

  useEffect(() => {
    if (!task.taskProjectTemplateSetting?.label_group_templates) return
    setData(task.taskProjectTemplateSetting.label_group_templates)
  }, [task.taskProjectTemplateSetting])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - 設定 - その他の設定 - プロジェクト設定 - テンプレート設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        設定
        &nbsp;&gt;&nbsp;
        その他の設定
        &nbsp;&gt;&nbsp;
        ユーザーテンプレート設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          ユーザーテンプレート設定
        </h1>
        <div className="tools">
          <div className="button_area">
            <button
              type="button"
              className="button"
              onClick={createTemplate}
            >
              <FontAwesomeIcon icon={['fas', 'plus']} />
              &nbsp;
              テンプレートを新規作成する
            </button>
          </div>
        </div>
      </section>
      <section className="page_description">
        <p>
          ユーザーテンプレートを作成・変更できます。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            {
              isLoading ? (
                <LoadingSquare>
                  <>
                    ユーザーテンプレートリストを読み込んでいます…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  {
                    data.length ? (
                      <>
                        <div className="template_table">
                          <div className="row_head">
                            <div>
                              編集
                            </div>
                            <div>
                              セット名
                            </div>
                            <div>
                              セット内容
                            </div>
                          </div>
                          {
                            data.map((detail) => (
                              <div
                                className="row"
                                key={`detail_${detail.name}`}
                              >
                                <div>
                                  <Tips
                                    description="編集する"
                                    className=""
                                    icon={false}
                                  >
                                    <>
                                      <FontAwesomeIcon
                                        icon={['fas', 'tools']}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => updateTemplate(detail?.id || 0)}
                                      />
                                    </>
                                  </Tips>
                                  <Tips
                                    description="削除する"
                                    className=""
                                    icon={false}
                                  >
                                    <>
                                      <FontAwesomeIcon
                                        icon={['fas', 'minus-circle']}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => openConfirmationModal("delete", detail?.id || 0, `ユーザーテンプレート:${detail.name}`)}
                                      />
                                    </>
                                  </Tips>
                                </div>
                                <div>
                                  <div
                                    className="color"
                                    style={{
                                      backgroundColor: detail.color,
                                    }}
                                  />
                                  {detail.name}
                                  {
                                    detail.description ? (
                                      <Tips
                                        description={detail.description.replace(/\n/g, "%br%")}
                                        className=""
                                        icon={false}
                                      >
                                        <>
                                          <FontAwesomeIcon
                                            icon={['far', 'comment-dots']}
                                          />
                                        </>
                                      </Tips>
                                    ) : (
                                      <></>
                                    )
                                  }
                                </div>
                                <div>
                                  {
                                    (detail.permission.viewable.teams.length || detail.permission.viewable.members.length) ? (
                                      <>
                                        <div>
                                          <div className="template_detail_title">
                                            プロジェクト表示チーム・メンバー
                                          </div>
                                          {
                                            detail.permission.viewable.teams.map((team) => (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    padding: "8px",
                                                    borderBottom: "solid 1px #CCCCCC",
                                                    backgroundColor: "#EEEEEE",
                                                  }}
                                                >
                                                  <div>
                                                    {`チーム「${team.name}」のマネージャー`}
                                                  </div>
                                                </div>
                                                <div
                                                  className="template_tips"
                                                  style={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {
                                                    task.taskProjectTemplateSetting?.teams.find((v) => v.id === team.id)?.managers.map((member) => (
                                                      <div
                                                        className="template_tip"
                                                        key={`viewable_team_member_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            ))
                                          }
                                          <div className="template_tips">
                                            {
                                              detail.permission.viewable.members.map((member) => (
                                                <div
                                                  key={`viewable_member_${member.name}`}
                                                  className="template_tip"
                                                >
                                                  {member.name}
                                                </div>
                                              ))
                                            }
                                          </div>
                                        </div>
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                  {
                                    (detail.permission.usable.teams.length || detail.permission.usable.members.length) ? (
                                      <>
                                        <div>
                                          <div className="template_detail_title">
                                            プロジェクト参加チーム・メンバー
                                          </div>
                                          {
                                            detail.permission.usable.teams.map((team) => (
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    padding: "8px",
                                                    borderBottom: "solid 1px #CCCCCC",
                                                    backgroundColor: "#EEEEEE",
                                                  }}
                                                >
                                                  <div>
                                                    {`チーム「${team.name}」のメンバー`}
                                                  </div>
                                                </div>
                                                <div
                                                  className="template_tips"
                                                  style={{
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {
                                                    task.taskProjectTemplateSetting?.teams.find((v) => v.id === team.id)?.members.map((member) => (
                                                      <div
                                                        className="template_tip"
                                                        key={`usable_team_member_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            ))
                                          }
                                          <div className="template_tips">
                                            {
                                              detail.permission.usable.members.map((member) => (
                                                <div
                                                  key={`usable_member_${member.name}`}
                                                  className="template_tip"
                                                >
                                                  {member.name}
                                                </div>
                                              ))
                                            }
                                          </div>
                                        </div>
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                  {
                                    detail.labels.length ? (
                                      <>
                                        <div>
                                          <div className="template_detail_title">
                                            業務
                                          </div>
                                          <div className="template_tips">
                                            {
                                              detail.labels.map((label) => (
                                                <div
                                                  key={`${detail.name}_${label.name}`}
                                                  className="template_tip"
                                                >
                                                  {label.name}
                                                </div>
                                              ))
                                            }
                                          </div>
                                        </div>
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="no_data_area">
                          <div className="no_data">
                            テンプレートはありません
                          </div>
                          <div className="no_data_button_area">
                            <button
                              type="button"
                              className="button"
                              onClick={createTemplate}
                            >
                              <FontAwesomeIcon icon={['fas', 'plus']} />
                              &nbsp;
                              テンプレートを新規作成する
                            </button>
                          </div>
                        </div>
                      </>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
      </section>
      {/* 確認モーダル */}
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
    </section>
  )
}

export default Index
